import { FC, useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { pdf } from '@react-pdf/renderer';
import { DownloadIcon } from '@heroicons/react/outline';

import Config from '../../lib/config';
import { parseMarkdown } from '../lib/markdown';
import UnauthedHeader from '../components/Headers/UnauthedHeader';
import PageContainer from '../components/Containers/PageContainer';
import Button from '../components/Button';
import Alert from '../components/Alert';
import IllustrationWarning from '../svgs/IllustrationWarning';
import Spinner from '../svgs/Spinner';
import { customToast } from '../components/ToastAlert/customToast';
import ProgramNotePDF from '../components/ProgramNotePDF';
import { handleProgramNotePdfDownload } from '../lib/pdf';
import { getFullDateTimeStamp } from '../lib/time';

const API_BASE = Config.REACT_APP_HOMECOMING_API_BASE_URI;
const NOTE_SHARE_URL = `${API_BASE}/api/v2/note-share`;

type ProviderBranding = {
  primary_color: string;
  secondary_color: string;
  wordmark_logo_url: string;
  square_logo_url: string;
};

type NoteSharePayload = {
  title?: string | null;
  body?: string | null;
  created_at?: string | null;
  created_by_name?: string | null;
  patient_first_name?: string | null;
  patient_full_name?: string | null;
  provider_name?: string | null;
  recipient_name?: string | null;
  expires_at?: string | null;
  branding?: ProviderBranding | null;
};

const NoteShare: FC = () => {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');

  const [payload, setPayload] = useState<NoteSharePayload | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isDownloading, setIsDownloading] = useState(false);

  useEffect(() => {
    if (!token) {
      setError('Missing link');
      setLoading(false);
      return;
    }
    let cancelled = false;
    setLoading(true);
    setError(null);
    fetch(`${NOTE_SHARE_URL}?token=${encodeURIComponent(token)}`)
      .then(async (res) => {
        if (!res.ok) {
          let message = 'Invalid or expired link';
          try {
            const d = await res.json();
            message = d?.message ?? message;
          } catch {
            const text = await res.text();
            if (text) message = text;
          }
          throw new Error(message);
        }
        return res.json();
      })
      .then((data: NoteSharePayload) => {
        if (!cancelled) {
          setPayload(data);
        }
      })
      .catch((err) => {
        if (!cancelled) {
          setError(err?.message ?? 'Invalid or expired link');
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [token]);

  const handleDownloadPdf = async () => {
    if (!token || !payload) return;

    try {
      setIsDownloading(true);

      const res = await fetch(`${NOTE_SHARE_URL}/download`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token }),
      });
      if (!res.ok) {
        let message = 'Unable to download PDF';
        try {
          const errorData = await res.json();
          message = errorData?.message ?? message;
        } catch {
          const text = await res.text();
          if (text) message = text;
        }
        throw new Error(message);
      }

      const patientName =
        payload.patient_full_name ?? payload.patient_first_name ?? 'Client';
      const wordmarkLogoUrl = payload.branding?.wordmark_logo_url || null;

      const programNote = {
        title: payload.title ?? 'Untitled note',
        note: payload.body ?? '',
        createdAt: payload.created_at ?? undefined,
        createdByProviderUser: {
          name: payload.created_by_name ?? 'Unknown',
        },
      };

      const blob = await pdf(
        <ProgramNotePDF
          programNote={programNote}
          patientName={patientName}
          wordmarkLogoUrl={wordmarkLogoUrl}
        />,
      ).toBlob();

      await handleProgramNotePdfDownload(blob, programNote, patientName);
    } catch (err) {
      customToast.error(
        err instanceof Error
          ? err.message
          : 'Unable to download PDF. Please try again.',
      );
    } finally {
      setIsDownloading(false);
    }
  };

  if (!token) {
    return (
      <>
        <UnauthedHeader />
        <PageContainer containerClassName="pt-16">
          <div className="flex flex-col items-center">
            <IllustrationWarning className="mt-14 mb-6 text-center text-orange-100" />
            <div className="mb-3 text-center font-serif text-subtitle font-light text-neutral-150">
              Invalid link
            </div>
            <div className="w-2/3 text-center text-paragraph text-neutral-125">
              This note link is missing a token. Please use the link you
              received.
            </div>
          </div>
        </PageContainer>
      </>
    );
  }

  if (loading) {
    return (
      <>
        <UnauthedHeader />
        <PageContainer containerClassName="pt-16">
          <div className="flex justify-center pt-24">
            <Spinner />
          </div>
        </PageContainer>
      </>
    );
  }

  if (error || !payload) {
    return (
      <>
        <UnauthedHeader />
        <PageContainer containerClassName="pt-16">
          <div className="flex flex-col items-center">
            <IllustrationWarning className="mt-14 mb-6 text-center text-orange-100" />
            <div className="mb-3 text-center font-serif text-subtitle font-light text-neutral-150">
              Uh oh!
            </div>
            <div className="w-2/3 text-center text-paragraph text-neutral-125">
              This link is invalid or expired. Please ask the person who sent it
              for a new link.
            </div>
          </div>
        </PageContainer>
      </>
    );
  }

  const branding = payload.branding ?? null;
  const patientName =
    payload.patient_full_name ?? payload.patient_first_name ?? null;

  const downloadButton = (
    <Button
      title={isDownloading ? 'Downloading…' : 'Download PDF'}
      size="small"
      IconComponent={isDownloading ? Spinner : DownloadIcon}
      iconPosition="left"
      disabled={isDownloading}
      onClick={handleDownloadPdf}
      style={
        branding?.secondary_color
          ? {
              backgroundColor: branding.secondary_color,
              borderColor: branding.secondary_color,
              color: '#fff',
            }
          : undefined
      }
    />
  );

  return (
    <>
      <UnauthedHeader
        branding={branding}
        actions={downloadButton}
        alignToContent
      />
      <PageContainer noPadding containerClassName="pt-16">
        <div className="mx-auto w-full max-w-5xl px-6 py-10 text-neutral-125">
          <div className="mb-6">
            <h1 className="mb-2 font-serif text-subtitle-small font-light text-green-150">
              {payload.title || 'Untitled note'}
            </h1>
            <p className="text-caption text-neutral-200">
              {[
                payload.created_by_name &&
                  `Created by ${payload.created_by_name}`,
                patientName && `for ${patientName}`,
                payload.provider_name && `· ${payload.provider_name}`,
              ]
                .filter(Boolean)
                .join(' ')}
            </p>
            {payload.created_at && (
              <p className="mt-1 text-small-caption text-neutral-110">
                {getFullDateTimeStamp(payload.created_at)}
              </p>
            )}
            {payload.expires_at && (
              <p className="mt-1 text-small-caption text-neutral-110">
                Link expires {getFullDateTimeStamp(payload.expires_at)}
              </p>
            )}
          </div>

          <Alert
            level="warning"
            message="Confidential health information"
            className="mb-4"
          >
            <p className="mt-1">
              This note contains confidential health information shared with you
              securely. Please do not forward this link or share its contents
              with anyone who is not authorized to view it. Access may be
              logged.
            </p>
          </Alert>

          <div className="w-full rounded-lg border border-neutral-75 bg-white p-5 shadow-100">
            <div
              className="prose prose-sm max-w-none text-body text-green-150"
              dangerouslySetInnerHTML={{
                __html: parseMarkdown(payload.body || ''),
              }}
            />
          </div>
        </div>
      </PageContainer>
    </>
  );
};

export default NoteShare;
